// Which participants are agents (the interpreter). Text streams count only when an agent sent
// them, because anyone may publish on any topic.
//
// The registry is created with the Room, before it connects, so no agent can arrive unseen.
// It remembers every agent seen in this room: notes an interpreter sent just before leaving
// still show. React reads it with useSyncExternalStore.

import { RoomEvent, type Room } from 'livekit-client';
import { useSyncExternalStore } from 'react';

export class AgentRegistry {
  private seen: ReadonlySet<string> = new Set();
  private readonly listeners = new Set<() => void>();

  constructor(private readonly room: Room) {
    room.on(RoomEvent.Connected, this.scan);
    room.on(RoomEvent.ParticipantConnected, this.scan);
    room.once(RoomEvent.Disconnected, () => {
      room.off(RoomEvent.Connected, this.scan);
      room.off(RoomEvent.ParticipantConnected, this.scan);
    });
  }

  private readonly scan = (): void => {
    const agents = [...this.room.remoteParticipants.values()]
      .filter((participant) => participant.isAgent)
      .map((participant) => participant.identity);
    if (agents.every((identity) => this.seen.has(identity))) return;
    this.seen = new Set([...this.seen, ...agents]);
    for (const listener of this.listeners) listener();
  };

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The same Set until a new agent is seen (a stable snapshot for React). */
  readonly snapshot = (): ReadonlySet<string> => this.seen;
}

export function useAgentIdentities(registry: AgentRegistry): ReadonlySet<string> {
  return useSyncExternalStore(registry.subscribe, registry.snapshot);
}
